import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

interface Location {
  id: string;
  branch: string;
  active?: boolean;
  [key: string]: any;
}

/**
 * Manages and displays a list of locations, allowing the user to change the active location and notifying the parent component when a selection is made.
 * Key methods:
 *   - changeView(index: number): Marks the chosen location as active (sets active on the selected item and clears it on others) and emits the selected location's id via the locationSelected output.
 * Lifecycle hooks:
 *   - No Angular lifecycle hooks are implemented in this component; it operates via input changes and user interactions.
 * @component
 * @selector app-location-management
 * @example
 *   <app-location-management [locations]="sampleLocations" (locationSelected)="onLocationSelected($event)"></app-location-management>
 * @input Location[] locations - Array of location objects to display and manage. Each location is expected to have at least an id and an active flag; the component updates the active flag when a location is selected.
 * @output EventEmitter<string> locationSelected - Emitted when a location is selected via changeView. The event payload is the selected location's id (string).
 */
@Component({
  selector: 'app-location-management',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './location-management.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./location-management.component.scss'],
})
export class LocationManagementComponent {
  @Input() locations: Location[] = [];
  @Output() locationSelected = new EventEmitter<string>();

  changeView(index: number) {
    this.locations.forEach((location, i) => {
      location.active = i === index;
    });
    this.locationSelected.emit(this.locations[index].id);
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
